/**
 * Single source of truth for in-app feature gates. Keep flags simple booleans
 * — anything more elaborate (per-user toggles, gradual rollouts, A/B) belongs
 * in a dedicated experimentation system, not here.
 *
 * Toggling: flip the value and rebuild. To preserve the option of overriding
 * at deploy time without a code change, each flag also reads an env var
 * (NEXT_PUBLIC_ prefix so it's available in the browser bundle); the env
 * value wins when set, otherwise the hardcoded default takes effect.
 *
 * Note: this only hides UI. Server routes, DB tables, and cron jobs related
 * to a flagged feature remain in place so we can re-enable instantly without
 * data migration.
 */

function flag(envName: string, defaultValue: boolean): boolean {
  const raw = process.env[envName];
  if (raw === undefined || raw === "") return defaultValue;
  return raw === "1" || raw.toLowerCase() === "true";
}

/**
 * Affiliates / referral program. Hidden from the consumer app while Jordi
 * runs referrals + coupons directly through Stripe. Flip to `true` (or set
 * NEXT_PUBLIC_FEATURE_AFFILIATES=1) to bring back: Settings → Affiliate tab,
 * landing-page footer links, and the public `/affiliate` pages.
 */
export const AFFILIATES_ENABLED = flag("NEXT_PUBLIC_FEATURE_AFFILIATES", false);

/**
 * Picking a *video* asset as source for AI generation. Image sources work
 * fully (image → image, image → video). Video sources currently only
 * extract the first frame, which is misleading UX — users expect
 * video-to-video. Until that pipeline ships (n8n branch for Runway / Sora
 * with full video input), the Videos chip + video asset cards on /generate
 * render locked with a "Coming soon" tooltip. Flip to `true` (or set
 * NEXT_PUBLIC_FEATURE_VIDEO_SOURCE=1) when the pipeline is ready.
 */
export const VIDEO_AS_SOURCE_ENABLED = flag(
  "NEXT_PUBLIC_FEATURE_VIDEO_SOURCE",
  false
);

/**
 * Carousel posting (2026-09-15): select 2+ photos → "Post" → choose separate
 * posts or one carousel, reorder the slides in the publish modal. The server
 * side is gated separately by PUBLISH_CAROUSEL (src/lib/publishing/carousel.ts).
 */
// Referință STATICĂ la process.env.NEXT_PUBLIC_…: Next.js inlinează în bundle-ul
// de browser doar accesul literal; `process.env[envName]` din flag() rămâne
// undefined pe client, deci steagul ar fi mereu OFF în componentele client.
export const CAROUSEL_ENABLED = flagValue(process.env.NEXT_PUBLIC_FEATURE_CAROUSEL, false);

function flagValue(raw: string | undefined, defaultValue: boolean): boolean {
  if (raw === undefined || raw === "") return defaultValue;
  return raw === "1" || raw.toLowerCase() === "true";
}
